/**
 * ProductsDemo — pure-CSS animated side-by-side demo of HyreDocs's two
 * products. Left pane shows a PDF placement agreement being filled +
 * signed; right pane shows an onboarding form being completed. Both
 * panes share an 8-second loop driven entirely by CSS keyframes (zero
 * JS animation loops, zero external assets). Honours `prefers-reduced-
 * motion` and only kicks off once the demo scrolls into the viewport.
 */
import { useEffect, useRef, useState } from 'react';

const HAS_REDUCED_MOTION =
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export default function ProductsDemo() {
  const [visible, setVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setVisible(true),
      { threshold: 0.25 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const playClass = visible && !HAS_REDUCED_MOTION ? 'pd-play' : '';

  return (
    <div ref={containerRef} className={`relative ${playClass}`} data-testid="products-demo">
      <Styles />

      <div className="grid lg:grid-cols-2 gap-5 lg:gap-6">
        <BrowserFrame
          accent="from-[#f97316] to-[#f59e0b]"
          urlText="hyredocs.app  ›  candidate  ›  placement-agreement.pdf"
          tagText="DOCUMENTS"
          tagClass="text-orange-700 bg-orange-100"
        >
          <DocumentsPane />
        </BrowserFrame>

        <BrowserFrame
          accent="from-violet-500 to-indigo-500"
          urlText="hyredocs.app  ›  forms  ›  onboarding"
          tagText="ONLINE FORMS"
          tagClass="text-violet-700 bg-violet-100"
        >
          <FormsPane />
        </BrowserFrame>
      </div>

      <div className="mt-5 flex items-center justify-center gap-1.5 text-xs text-slate-500">
        <span className="pd-dot" />
        <span className="pd-dot pd-dot-2" />
        <span className="pd-dot pd-dot-3" />
        <span className="ml-2">Both flows write back to the same Bullhorn record on completion.</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- */
/* Browser-chrome wrapper                                         */
/* ------------------------------------------------------------- */
function BrowserFrame({ accent, urlText, tagText, tagClass, children }) {
  return (
    <div className="relative">
      <div className={`absolute -inset-3 bg-gradient-to-br ${accent} opacity-10 blur-2xl rounded-3xl pointer-events-none`} />
      <div className="relative rounded-2xl bg-white shadow-xl border border-slate-200 overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 bg-slate-50/80">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
          </div>
          <div className="flex-1 bg-white rounded-md px-3 py-1 text-[11px] text-slate-500 font-mono border border-slate-200/70 truncate">
            {urlText}
          </div>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${tagClass}`}>
            {tagText}
          </span>
        </div>
        <div className="relative h-[470px] bg-gradient-to-b from-slate-50 to-white">
          {children}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- */
/* DOCUMENTS pane — Placement Agreement                            */
/* ------------------------------------------------------------- */
function DocumentsPane() {
  return (
    <div className="relative h-full p-4">
      <div className="relative bg-white rounded-lg border border-slate-200 shadow-sm h-full overflow-hidden">
        {/* Document header */}
        <div className="px-5 pt-4 pb-3 border-b border-slate-100">
          <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Schedule A</div>
          <div className="text-sm font-bold text-slate-900 mt-0.5">Placement Agreement</div>
        </div>

        {/* Mock body lines */}
        <div className="px-5 pt-3 space-y-1.5">
          <div className="h-1.5 w-full bg-slate-100 rounded-sm" />
          <div className="h-1.5 w-[92%] bg-slate-100 rounded-sm" />
          <div className="h-1.5 w-[55%] bg-slate-100 rounded-sm" />
        </div>

        {/* Row 1: Name + Start date (typewriter) */}
        <div className="absolute left-4 top-[112px] w-[46%]">
          <FieldLabel>Full name</FieldLabel>
          <div className="h-8 rounded border-2 border-orange-400 bg-orange-50 px-2 flex items-center text-[13px] text-slate-800 font-medium overflow-hidden">
            <span className="pd-typed pd-typed-doc-name whitespace-nowrap">Aisha Williams</span>
            <span className="pd-caret" />
          </div>
        </div>
        <div className="absolute right-4 top-[112px] w-[46%]">
          <FieldLabel>Start date</FieldLabel>
          <div className="h-8 rounded border-2 border-orange-400 bg-orange-50 px-2 flex items-center text-[13px] text-slate-800 font-medium overflow-hidden">
            <span className="pd-typed pd-typed-doc-date whitespace-nowrap">15 / 03 / 2026</span>
          </div>
        </div>

        {/* Row 2: NI number + Postcode (Bullhorn pre-fill — fade in with badge) */}
        <div className="absolute left-4 top-[178px] w-[46%]">
          <FieldLabel>NI number</FieldLabel>
          <div className="relative h-8 rounded border-2 border-orange-400 bg-orange-50 px-2 flex items-center text-[13px] text-slate-800 font-medium overflow-hidden">
            <span className="pd-prefill pd-prefill-a">QQ 12 34 56 C</span>
            <span className="pd-prefill-badge">from Bullhorn</span>
          </div>
        </div>
        <div className="absolute right-4 top-[178px] w-[46%]">
          <FieldLabel>Postcode</FieldLabel>
          <div className="relative h-8 rounded border-2 border-orange-400 bg-orange-50 px-2 flex items-center text-[13px] text-slate-800 font-medium overflow-hidden">
            <span className="pd-prefill pd-prefill-b">SW1A 1AA</span>
            <span className="pd-prefill-badge">from Bullhorn</span>
          </div>
        </div>

        {/* Right-to-work checkbox */}
        <div className="absolute left-4 top-[244px] flex items-center gap-2">
          <div className="w-4 h-4 rounded border-2 border-orange-400 bg-white flex items-center justify-center pd-checkbox">
            <svg className="w-3 h-3 pd-check" viewBox="0 0 16 16">
              <path d="M3 8.5l3 3 7-7" fill="none" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <span className="text-[11px] text-slate-700">I confirm I have the right to work in the UK</span>
        </div>

        {/* Signature box */}
        <div className="absolute left-4 right-4 bottom-12">
          <FieldLabel>Signature</FieldLabel>
          <div className="h-[78px] rounded border-2 border-dashed border-orange-400 bg-white relative overflow-hidden">
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 78" preserveAspectRatio="none">
              <path
                d="M20,54 C40,18 60,60 95,40 C125,24 145,60 175,42 C205,28 225,60 260,40 C295,26 320,54 345,42 C365,36 380,40 385,46"
                fill="none"
                stroke="#1e293b"
                strokeWidth="2.5"
                strokeLinecap="round"
                className="pd-sig-path pd-sig-doc"
              />
            </svg>
          </div>
        </div>

        <div className="pd-cursor pd-cursor-doc"><CursorIcon /></div>

        <div className="absolute bottom-2 left-0 right-0 text-center text-[9px] text-slate-400 font-mono">
          Page 1 of 1  ·  HyreDocs
        </div>
      </div>

      <div className="pd-toast-doc absolute right-3 bottom-3 px-3.5 py-2.5 rounded-xl shadow-lg bg-slate-900 text-white text-xs font-medium flex items-center gap-2 z-10">
        <span className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
          <svg width="10" height="10" viewBox="0 0 16 16"><path d="M3 8.5l3 3 7-7" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </span>
        Signed · synced to Bullhorn
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- */
/* FORMS pane — Onboarding form                                    */
/* ------------------------------------------------------------- */
function FormsPane() {
  return (
    <div className="relative h-full px-6 py-5">
      <div className="mb-3.5">
        <div className="text-[10px] font-bold uppercase tracking-wider text-violet-700 mb-0.5">Onboarding</div>
        <div className="text-base font-bold text-slate-900">New Starter Form</div>
      </div>

      <div className="space-y-3">
        {/* Full name (typewriter) */}
        <FormField label="Full name">
          <span className="pd-typed pd-typed-form-name whitespace-nowrap text-[13px] text-slate-800">Aisha Williams</span>
          <span className="pd-caret pd-caret-violet" />
        </FormField>

        {/* Email (typewriter) */}
        <FormField label="Email">
          <span className="pd-typed pd-typed-form-email whitespace-nowrap text-[13px] text-slate-800">aisha.w@email.com</span>
        </FormField>

        {/* Date of birth — pre-filled from Bullhorn with fade-in + badge */}
        <div>
          <FieldLabel violet>Date of birth</FieldLabel>
          <div className="relative h-9 rounded-lg border border-slate-200 bg-white px-3 flex items-center overflow-hidden">
            <span className="pd-prefill pd-prefill-form-dob text-[13px] text-slate-800">12 / 06 / 1994</span>
            <span className="pd-prefill-badge pd-prefill-badge-violet">from Bullhorn</span>
          </div>
        </div>

        {/* Right to work — dropdown that "selects" a value */}
        <div>
          <FieldLabel violet>Right to work</FieldLabel>
          <div className="relative h-9 rounded-lg border border-slate-200 bg-white px-3 flex items-center justify-between overflow-hidden">
            <span className="text-[13px]">
              <span className="text-slate-400 pd-dropdown-placeholder">Select an option…</span>
              <span className="pd-dropdown-value text-slate-800 font-medium">UK Citizen</span>
            </span>
            <svg width="14" height="14" viewBox="0 0 16 16" className="text-slate-400">
              <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>

        {/* Rating */}
        <div>
          <FieldLabel violet>How happy are you with the role?</FieldLabel>
          <div className="flex items-center gap-1.5">
            {[0, 1, 2, 3, 4].map((i) => <Star key={i} index={i} />)}
          </div>
        </div>
      </div>

      {/* Submit button */}
      <div className="absolute left-6 right-6 bottom-5">
        <div className="relative w-full h-10 rounded-lg bg-gradient-to-r from-violet-500 to-indigo-500 shadow-md flex items-center justify-center pd-submit overflow-hidden">
          <span className="pd-submit-label text-white text-sm font-semibold">Submit form</span>
          <span className="pd-submit-success absolute inset-0 flex items-center justify-center text-white text-sm font-semibold">
            <svg width="14" height="14" viewBox="0 0 16 16" className="inline-block mr-1.5"><path d="M3 8.5l3 3 7-7" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            Synced to Bullhorn
          </span>
        </div>
      </div>

      <div className="pd-cursor pd-cursor-form"><CursorIcon /></div>
    </div>
  );
}

/* ------------------------------------------------------------- */
/* Tiny presentational helpers                                     */
/* ------------------------------------------------------------- */
function FieldLabel({ children, violet }) {
  return (
    <div
      className={`text-[9px] font-semibold uppercase tracking-wider mb-1 ${violet ? 'text-slate-500' : 'text-slate-500'}`}
    >
      {children}
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <FieldLabel violet>{label}</FieldLabel>
      <div className="h-9 rounded-lg border border-slate-200 bg-white px-3 flex items-center overflow-hidden">
        {children}
      </div>
    </div>
  );
}

function Star({ index }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      className="pd-star"
      style={{ animationDelay: `${index * 0.12}s` }}
    >
      <path
        d="M12 2.5l2.9 6.5 7.1.7-5.4 4.8 1.6 7-6.2-3.6L5.8 21.5l1.6-7L2 9.7l7.1-.7z"
        fill="currentColor"
      />
    </svg>
  );
}

function CursorIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" className="drop-shadow-md">
      <path
        d="M4 2l6 18 3-7 7-3z"
        fill="white"
        stroke="#0f172a"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ------------------------------------------------------------- */
/* Styles                                                          */
/* ------------------------------------------------------------- */
function Styles() {
  return (
    <style>{`
      /* === Typewriter: max-width steps animation === */
      .pd-typed {
        display: inline-block;
        max-width: 0;
        overflow: hidden;
        vertical-align: bottom;
      }
      .pd-play .pd-typed-doc-name   { animation: pd-type-name-doc  8s steps(14, end) infinite; }
      .pd-play .pd-typed-doc-date   { animation: pd-type-date-doc  8s steps(14, end) infinite; }
      .pd-play .pd-typed-form-name  { animation: pd-type-name-form 8s steps(14, end) infinite; }
      .pd-play .pd-typed-form-email { animation: pd-type-email-form 8s steps(17, end) infinite; }

      @keyframes pd-type-name-doc {
        0%, 3%    { max-width: 0; }
        18%, 100% { max-width: 14ch; }
      }
      @keyframes pd-type-date-doc {
        0%, 19%   { max-width: 0; }
        30%, 100% { max-width: 14ch; }
      }
      @keyframes pd-type-name-form {
        0%, 4%    { max-width: 0; }
        20%, 100% { max-width: 14ch; }
      }
      @keyframes pd-type-email-form {
        0%, 21%   { max-width: 0; }
        34%, 100% { max-width: 17ch; }
      }

      /* === Caret === */
      .pd-caret {
        display: inline-block;
        width: 1.5px;
        height: 14px;
        background: #f97316;
        margin-left: 1px;
        opacity: 0;
      }
      .pd-caret-violet { background: #7c3aed; }
      .pd-play .pd-caret { animation: pd-caret-blink 0.9s steps(2) infinite; }
      @keyframes pd-caret-blink {
        0%, 49%  { opacity: 1; }
        50%, 100%{ opacity: 0; }
      }

      /* === Bullhorn pre-fill fade-in === */
      .pd-prefill { opacity: 0; }
      .pd-play .pd-prefill-a       { animation: pd-prefill 8s ease-out infinite; }
      .pd-play .pd-prefill-b       { animation: pd-prefill 8s ease-out infinite; animation-delay: 0.2s; }
      .pd-play .pd-prefill-form-dob{ animation: pd-prefill-form 8s ease-out infinite; }

      @keyframes pd-prefill {
        0%, 34%   { opacity: 0; transform: translateY(2px); }
        40%, 100% { opacity: 1; transform: translateY(0); }
      }
      @keyframes pd-prefill-form {
        0%, 40%   { opacity: 0; transform: translateY(2px); }
        46%, 100% { opacity: 1; transform: translateY(0); }
      }

      .pd-prefill-badge {
        position: absolute;
        right: 5px;
        top: 50%;
        transform: translateY(-50%);
        font-size: 7px;
        font-weight: 700;
        letter-spacing: 0.4px;
        text-transform: uppercase;
        padding: 2px 5px;
        border-radius: 4px;
        background: #fff7ed;
        color: #c2410c;
        border: 1px solid #fed7aa;
        opacity: 0;
      }
      .pd-prefill-badge-violet {
        background: #f5f3ff;
        color: #6d28d9;
        border-color: #ddd6fe;
      }
      .pd-play .pd-prefill-badge {
        animation: pd-badge 8s ease-out infinite;
      }
      @keyframes pd-badge {
        0%, 36%   { opacity: 0; transform: translateY(-50%) scale(0.85); }
        44%, 80%  { opacity: 1; transform: translateY(-50%) scale(1); }
        88%, 100% { opacity: 0; transform: translateY(-50%) scale(0.95); }
      }

      /* === Right-to-work checkbox === */
      .pd-checkbox { transition: none; }
      .pd-play .pd-checkbox { animation: pd-checkbox-tick 8s ease-out infinite; }
      @keyframes pd-checkbox-tick {
        0%, 46%   { background: #fff; border-color: #fb923c; transform: scale(1); }
        50%       { background: #fff7ed; border-color: #f97316; transform: scale(1.18); }
        54%, 100% { background: #fff7ed; border-color: #f97316; transform: scale(1); }
      }
      .pd-check {
        stroke-dasharray: 28;
        stroke-dashoffset: 28;
      }
      .pd-play .pd-check path { animation: pd-check-draw 8s ease-out infinite; }
      @keyframes pd-check-draw {
        0%, 48%   { stroke-dashoffset: 28; }
        56%, 100% { stroke-dashoffset: 0; }
      }

      /* === Form dropdown: placeholder → value swap === */
      .pd-dropdown-placeholder { opacity: 1; }
      .pd-dropdown-value       { opacity: 0; position: absolute; left: 12px; }
      .pd-play .pd-dropdown-placeholder { animation: pd-fade-out-late 8s ease-out infinite; }
      .pd-play .pd-dropdown-value       { animation: pd-fade-in-late  8s ease-out infinite; }
      @keyframes pd-fade-out-late {
        0%, 50%  { opacity: 1; }
        55%, 100%{ opacity: 0; }
      }
      @keyframes pd-fade-in-late {
        0%, 50%   { opacity: 0; transform: translateY(2px); }
        56%, 100% { opacity: 1; transform: translateY(0); }
      }

      /* === Signature path === */
      .pd-sig-path {
        stroke-dasharray: 900;
        stroke-dashoffset: 900;
      }
      .pd-play .pd-sig-doc  { animation: pd-draw-doc 8s ease-in-out infinite; }
      @keyframes pd-draw-doc {
        0%, 62%   { stroke-dashoffset: 900; }
        80%, 100% { stroke-dashoffset: 0; }
      }

      /* === Cursors (top/left percentages relative to parent) === */
      .pd-cursor {
        position: absolute;
        top: 0; left: 0;
        opacity: 0;
        pointer-events: none;
        z-index: 5;
      }
      .pd-play .pd-cursor-doc  { animation: pd-cursor-doc 8s ease-in-out infinite; }
      .pd-play .pd-cursor-form { animation: pd-cursor-form 8s ease-in-out infinite; }

      @keyframes pd-cursor-doc {
        0%, 3%    { opacity: 0; top: 8%;  left: 18%; }
        6%        { opacity: 1; top: 30%; left: 22%; }    /* name field */
        20%       { opacity: 1; top: 30%; left: 22%; }
        26%       { opacity: 1; top: 30%; left: 70%; }    /* date field */
        34%       { opacity: 1; top: 30%; left: 70%; }
        40%       { opacity: 1; top: 45%; left: 22%; }    /* NI prefill */
        48%       { opacity: 1; top: 52%; left: 12%; }    /* checkbox */
        58%       { opacity: 1; top: 78%; left: 12%; }    /* signature start */
        72%       { opacity: 1; top: 78%; left: 80%; }    /* signature end */
        82%, 100% { opacity: 0; top: 78%; left: 80%; }
      }
      @keyframes pd-cursor-form {
        0%, 3%    { opacity: 0; top: 10%; left: 22%; }
        7%        { opacity: 1; top: 22%; left: 30%; }    /* name */
        22%       { opacity: 1; top: 22%; left: 30%; }
        28%       { opacity: 1; top: 36%; left: 30%; }    /* email */
        40%       { opacity: 1; top: 36%; left: 30%; }
        46%       { opacity: 1; top: 50%; left: 30%; }    /* dob prefill */
        54%       { opacity: 1; top: 64%; left: 30%; }    /* dropdown */
        66%       { opacity: 1; top: 78%; left: 25%; }    /* rating */
        80%       { opacity: 1; top: 78%; left: 60%; }
        87%       { opacity: 1; top: 90%; left: 48%; }    /* submit */
        93%, 100% { opacity: 0; top: 90%; left: 48%; }
      }

      /* === Toast === */
      .pd-toast-doc { opacity: 0; transform: translateY(20px); }
      .pd-play .pd-toast-doc { animation: pd-toast 8s ease-out infinite; }
      @keyframes pd-toast {
        0%, 80%   { opacity: 0; transform: translateY(20px); }
        86%, 96%  { opacity: 1; transform: translateY(0); }
        100%      { opacity: 0; transform: translateY(20px); }
      }

      /* === Stars === */
      .pd-star { color: #e5e7eb; }
      .pd-play .pd-star { animation: pd-star-fill 8s ease-out infinite; }
      @keyframes pd-star-fill {
        0%, 66%   { color: #e5e7eb; transform: scale(1); }
        70%       { color: #f59e0b; transform: scale(1.25); }
        74%, 92%  { color: #f59e0b; transform: scale(1); }
        96%, 100% { color: #e5e7eb; transform: scale(1); }
      }

      /* === Submit morph === */
      .pd-play .pd-submit         { animation: pd-btn     8s ease-out infinite; }
      .pd-play .pd-submit-label   { animation: pd-label   8s ease-out infinite; }
      .pd-play .pd-submit-success { animation: pd-success 8s ease-out infinite; opacity: 0; }
      .pd-submit-success { opacity: 0; }

      @keyframes pd-btn {
        0%, 88%   { box-shadow: 0 4px 12px rgba(124, 58, 237, 0.25); transform: scale(1); }
        91%       { transform: scale(0.96); }
        95%, 100% { transform: scale(1); box-shadow: 0 6px 18px rgba(16, 185, 129, 0.35); }
      }
      @keyframes pd-label {
        0%, 90%   { opacity: 1; }
        93%, 100% { opacity: 0; }
      }
      @keyframes pd-success {
        0%, 90%   { opacity: 0; }
        94%, 100% { opacity: 1; }
      }

      /* === Sync dots === */
      .pd-dot {
        display: inline-block;
        width: 6px; height: 6px; border-radius: 50%;
        background: #cbd5e1;
      }
      .pd-play .pd-dot   { animation: pd-pulse 8s ease-out infinite; }
      .pd-play .pd-dot-2 { animation-delay: 0.2s; }
      .pd-play .pd-dot-3 { animation-delay: 0.4s; }
      @keyframes pd-pulse {
        0%, 80%  { background: #cbd5e1; transform: scale(1); }
        85%, 95% { background: #10b981; transform: scale(1.2); }
        100%     { background: #cbd5e1; transform: scale(1); }
      }

      @media (prefers-reduced-motion: reduce) {
        .pd-play * { animation: none !important; }
        .pd-play .pd-typed { max-width: 100% !important; }
        .pd-play .pd-sig-path { stroke-dashoffset: 0 !important; }
        .pd-play .pd-star { color: #f59e0b !important; }
        .pd-play .pd-prefill { opacity: 1 !important; }
        .pd-play .pd-prefill-badge { opacity: 1 !important; }
        .pd-play .pd-check path { stroke-dashoffset: 0 !important; }
        .pd-play .pd-dropdown-placeholder { opacity: 0 !important; }
        .pd-play .pd-dropdown-value { opacity: 1 !important; }
      }
    `}</style>
  );
}
